import type {
	DraftWorkItem,
	IssueTypeWithFieldsConfiguration,
} from '@atlassian/jira-create-work-items/useAiCreateProjectContext/types.tsx';

const ISSUEKEY_REGEX = /^[A-Za-z0-9]+-\d+$/;
const BASE_URL_REGEX = /^(https:\/\/[^\/]+)/;

export const createWorkItemAri = (cloudId: string, issueKey: string): string =>
	`ari:cloud:jira:${cloudId}:issue/${issueKey}`;

// TODO: CRCS-4482 Improve this check to see if a parent issue key has already been created
export const isValidJiraIssueKey = (issueKey: string): boolean => {
	return issueKey.match(ISSUEKEY_REGEX) !== null;
};

export const isSystemField = (fieldId?: string): boolean => {
	if (!fieldId) {
		return false;
	}
	return !fieldId.startsWith('customfield_');
};

/**
 * Checks if the draft work item is a subtask.
 */
export const isSubTask = (draftWorkItem: DraftWorkItem): boolean => {
	if (!draftWorkItem?.fields) {
		return false;
	}
	const hierarchyLevel = (
		draftWorkItem.fields.issuetype as IssueTypeWithFieldsConfiguration | undefined
	)?.hierarchyLevel;
	return hierarchyLevel !== undefined && hierarchyLevel < 0;
};

/**
 * Checks if the draft work item is a subtask without a created parent.
 */
export const isSubTaskWithoutCreatedParent = (draftWorkItem: DraftWorkItem): boolean => {
	if (!draftWorkItem?.fields) {
		return false;
	}
	const parent = draftWorkItem.fields?.parent as string | undefined;

	const isSubTaskWithoutParent =
		isSubTask(draftWorkItem) && (!parent || !isValidJiraIssueKey(parent));

	return isSubTaskWithoutParent;
};

// Extract cloudUrl from the first issue
export const extractCloudUrl = (link: string): string => {
	return link.match(BASE_URL_REGEX)?.[0] ?? '';
};

/**
 * Classifies an error to a reason string used for analytics.
 */
export const getJiraCreateErrorReason = (error?: Error): string => {
	if (!error) {
		return 'unknown';
	}
	if (error.name === 'ChunkLoadError') {
		return 'chunk_load';
	}

	const message = error.message?.toLowerCase() ?? '';
	if (message.includes('network') || message.includes('fetch')) {
		return 'network';
	}
	if (message.includes('timeout')) {
		return 'timeout';
	}
	if (message.includes('permission') || message.includes('forbidden')) {
		return 'permission';
	}
	return 'render_error';
};
